/**
 * @fileoverview ConsentChecker Web Component for location-based consent management
 * 
 * This component handles geo-location detection and consent management for e-commerce stores
 * that need to comply with state-specific regulations. It uses IP geolocation to detect user
 * location and manages consent workflows for cart checkout processes by adding line item properties.
 * 
 * Key Features:
 * - Automatic IP-based geolocation detection
 * - Consent state verification (e.g., CA, IL, etc.)
 * - Cart monitoring for consent-required transactions
 * - Checkout interception with consent validation
 * - Line item property-based consent storage for auditability
 * - Event-driven architecture for custom integrations
 * 
 * @version 2.0.0
 * @author Octavian Contis @ Enchant Digital
 */

/**
 * @typedef {Object} LocationData
 * @property {string} region - Full region/state name
 * @property {string} region_code - Two-letter state code (e.g., "CA", "IL")
 * @property {string} country - Country name
 * @property {boolean} success - API call success status
 */

/**
 * @typedef {Object} ConsentData
 * @property {string} timestamp - ISO timestamp when consent was given
 * @property {LocationData} location - User's location data
 * @property {boolean} consentGiven - Whether consent has been provided
 */

/**
 * @typedef {Object} CartItem
 * @property {string|number} variant_id - Product variant ID
 * @property {string|number} product_id - Product ID
 * @property {string|number} id - Line item ID (for cart updates)
 * @property {string} handle - Product handle/slug
 * @property {Object} [product] - Product object with nested ID
 * @property {Object} [variant] - Variant object with product_id
 * @property {Object} [properties] - Line item properties
 */

/**
 * ConsentChecker Web Component
 * 
 * A custom HTML element that automatically detects user location and manages
 * consent workflows for state-specific compliance requirements using line item properties.
 * 
 * How it works:
 * 1. Detects user location via IP geolocation API
 * 2. Checks if user is from a consent-required state
 * 3. Monitors cart for items missing consent properties
 * 4. Shows consent checkbox and blocks checkout until consent is given
 * 5. Adds consent as line item properties to all cart items for auditability
 * 
 * @class ConsentChecker
 * @extends HTMLElement
 * 
 * @example
 * // HTML Usage
 * <consent-checker 
 *   data-consent-states="CA,IL,NY"
 *   data-consent-text="I acknowledge..."
 *   data-api-key="your-api-key"
 *   data-product-ids="123,456,789"
 *   data-debug="true">
 * </consent-checker>
 */
class ConsentChecker extends HTMLElement {
  /**
   * Creates an instance of ConsentChecker
   * Initializes all properties with default values
   */
  constructor() {
    super();
    
    /** @type {string[]} Array of state codes requiring consent (e.g., ['CA', 'IL']) */
    this.consentStates = [];
    
    /** @type {string} HTML text to display in consent checkbox */
    this.consentText = '';
    
    /** @type {string[]} Array of product IDs that require consent */
    this.productIds = [];
    
    /** @type {string} API key for IP geolocation service */
    this.apiKey = '';
    
    /** @type {LocationData|null} Current user's detected location */
    this.userLocation = null;
    
    /** @type {boolean} Flag to prevent concurrent location checks */
    this.isChecking = false;
    
    /** @type {string} Cookie name for caching location data (24 hour expiry) */
    this.locationCookieName = 'user_location_cache';
    
    /** @type {boolean} Enable debug logging and alerts */
    this.debug = false;
    
    /** @type {boolean} Flag to track if consent is currently being processed */
    this.isProcessingConsent = false;
    
    /** @type {boolean} Flag to prevent concurrent cart checks */
    this.isCheckingCart = false;
    
    /** @type {number} Timestamp of last cart check to prevent rapid re-checks */
    this.lastCartCheckTime = 0;
    
    /** @type {boolean} Flag to track if consent checkbox is currently shown */
    this.consentCheckboxShown = false;
    
    /** @type {HTMLElement|null} Cached cart drawer element */
    this._cartDrawer = null;
    
    /** @type {HTMLElement|null} Cached main cart element */
    this._mainCart = null;
    
    /** @type {HTMLElement|null} Cached cart page consent container */
    this._cartPageConsent = null;
    
    /** @type {MutationObserver|null} Cart drawer observer */
    this._drawerObserver = null;
    
    /** @type {MutationObserver|null} Cart page observer */
    this._cartPageObserver = null;
  }

  /**
   * Web Component lifecycle method - called when element is removed from DOM
   * Cleans up observers and event listeners
   */
  disconnectedCallback() {
    // Disconnect observers
    if (this._drawerObserver) {
      this._drawerObserver.disconnect();
      this._drawerObserver = null;
    }
    if (this._cartPageObserver) {
      this._cartPageObserver.disconnect();
      this._cartPageObserver = null;
    }
    
    // Clear cached elements
    this._cartDrawer = null;
    this._mainCart = null;
    this._cartPageConsent = null;
  }

  /**
   * Web Component lifecycle method - called when element is added to DOM
   * Parses data attributes and initializes the consent checking process
   */
  connectedCallback() {
    // Parse consent states from data attribute
    this.consentStates = this.getAttribute('data-consent-states')
      ?.split(',')
      .map(state => state.trim().toUpperCase()) || [];
    
    this.consentText = this.getAttribute('data-consent-text') || '';
    this.apiKey = this.getAttribute('data-api-key') || '';
    this.debug = this.getAttribute('data-debug') === 'true';
    
    // Parse product IDs that require consent
    const productIdsData = this.getAttribute('data-product-ids');
    this.productIds = productIdsData ? 
      productIdsData.split(',').map(id => id.trim()).filter(id => id) : [];
    
    // Start the consent checking process
    this.checkUserLocation();
    
    // Set up cart monitoring
    this.initCartConsentMonitoring();
  }

  /**
   * Detects user location using IP geolocation with intelligent caching
   * 
   * Cache Strategy:
   * 1. Check location cache cookie (24 hours) - contains just location data
   * 2. Make fresh API call if no cache available
   * 
   * @async
   * @returns {Promise<void>}
   */
  async checkUserLocation() {
    if (this.debug === true) console.log('🔍 ConsentChecker: Starting location check...');
    
    // Prevent concurrent checks and validate configuration
    if (this.isChecking || this.consentStates.length === 0 || !this.apiKey) return;
    
    this.isChecking = true;
    
    try {
      // Check location cache (24 hours)
      const locationCookie = this.getCookie(this.locationCookieName);
      if (locationCookie) {
        const locationData = JSON.parse(locationCookie);
        if (this.debug === true) console.log('🍪 Using location cookie:', locationData);
        this.handleLocationData(locationData);
        return;
      }

      // Fresh API call
      if (this.debug === true) console.log('🌐 Making fresh API call...');
      const response = await fetch(`https://ipwhois.pro/?key=${this.apiKey}`);
      const locationData = await response.json();
      
      if (locationData.success) {
        if (this.debug === true) console.log('📍 Fresh API result:', locationData);
        this.setCookie(this.locationCookieName, JSON.stringify(locationData), 1);
        this.handleLocationData(locationData);
      }
    } catch (error) {
      console.error('💥 Error detecting location:', error);
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Processes location data and triggers appropriate consent workflows
   * 
   * @param {LocationData} locationData - Location data from API or cache
   */
  handleLocationData(locationData) {
    this.userLocation = locationData;
    
    // Determine if user is from a consent-required state
    const requiresConsent = this.isFromConsentState(locationData);
    
    // Show consent alert if required and enabled (debug mode only)
    if (requiresConsent && this.debug) {
      this.showConsentAlert(locationData);
    }
    
    // Emit custom event for external listeners
    this.dispatchEvent(new CustomEvent('location-checked', {
      detail: {
        location: locationData,
        requiresConsent: requiresConsent,
        consentStates: this.consentStates,
        productIds: this.productIds
      },
      bubbles: true
    }));

    // Check cart for consent requirements
    if (requiresConsent) {
      this.checkCartForConsentProducts();
    }
  }

  /**
   * Checks if user's location requires consent based on state code
   * 
   * @param {LocationData} locationData - User's location data
   * @returns {boolean} True if user is from a consent-required state
   */
  isFromConsentState(locationData) {
    if (!locationData.region_code) {
      if (this.debug) console.log('⚠️ No region_code in location data:', locationData);
      return false;
    }
    
    const userState = locationData.region_code.toUpperCase();
    const requiresConsent = this.consentStates.includes(userState);
    
    if (this.debug) {
      console.log('🔍 Checking consent requirement:', {
        userState,
        consentStates: this.consentStates,
        requiresConsent
      });
    }
    
    return requiresConsent;
  }

  /**
   * Logs consent requirement to console (debug mode only)
   * 
   * @param {LocationData} locationData - User's location data
   */
  showConsentAlert(locationData) {
    if (this.debug) {
      const userState = locationData.region || locationData.region_code;
      const userCountry = locationData.country;
      const consentTextClean = this.consentText.replace(/<[^>]*>/g, '');
      
      console.log('⚠️ Consent Required');
      console.log(`📍 Location: ${userState}, ${userCountry}`);
      console.log(`📝 Consent Text: ${consentTextClean}`);
      console.log('This state requires special consent for certain products and services.');
    }
  }

  /**
   * Sets up event listeners for cart updates and changes
   * Monitors various cart events to trigger consent checks when needed
   */
  initCartConsentMonitoring() {
    // Cache cart elements
    this._cartDrawer = document.querySelector('cart-drawer');
    this._mainCart = document.querySelector('main-cart');
    
    // Debounced cart check function
    const debouncedCheck = () => {
      setTimeout(() => this.checkCartForConsentProducts(), 100);
    };

    // Listen for Shopify cart update events (single handler for all)
    ['cart:updated', 'cart:change'].forEach(eventName => {
      document.addEventListener(eventName, debouncedCheck, { passive: true });
    });

    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.checkCartForConsentProducts(), 200);
    }, { passive: true });

    // Listen for theme-specific cart events
    if (window.theme?.pubsub) {
      window.theme.pubsub.subscribe('cartUpdate', debouncedCheck);
    }

    // Listen for cart drawer open/close events
    if (this._cartDrawer) {
      // Use MutationObserver to detect when drawer becomes visible
      this._drawerObserver = new MutationObserver((mutations) => {
        // Only check if drawer became visible (not hidden)
        const becameVisible = mutations.some(mutation => 
          mutation.type === 'attributes' && 
          mutation.attributeName === 'hidden' &&
          !this._cartDrawer.hasAttribute('hidden')
        );
        
        if (becameVisible) {
          setTimeout(() => this.checkCartForConsentProducts(), 300);
        }
      });
      
      this._drawerObserver.observe(this._cartDrawer, {
        attributes: true,
        attributeFilter: ['hidden']
      });

      // Listen for drawer show events
      this._cartDrawer.addEventListener('drawer:show', debouncedCheck, { passive: true });
    }

    // Listen for main-cart element to be ready (for cart page)
    if (this._mainCart) {
      // Use MutationObserver to detect when cart page content is loaded
      // But ignore changes to consent containers to prevent loops
      this._cartPageObserver = new MutationObserver((mutations) => {
        // Ignore mutations caused by our own consent container changes
        const hasConsentContainerChanges = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && node.hasAttribute && node.hasAttribute('data-consent-container')
          ) || Array.from(mutation.removedNodes).some(node =>
            node.nodeType === 1 && node.hasAttribute && node.hasAttribute('data-consent-container')
          );
        });
        
        if (hasConsentContainerChanges) {
          if (this.debug) console.log('⏸️ Ignoring mutation from consent container changes');
          return;
        }
        
        // Check if checkout button exists (indicates cart page is fully loaded)
        if (this._mainCart.querySelector('button[name="checkout"]')) {
          setTimeout(() => this.checkCartForConsentProducts(), 200);
        }
      });
      
      this._cartPageObserver.observe(this._mainCart, {
        childList: true,
        subtree: true
      });
    }

    // Initial cart check on page load (single check with delay)
    const initialCheck = () => {
      setTimeout(() => this.checkCartForConsentProducts(), 500);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialCheck, { once: true });
    } else {
      initialCheck();
    }
  }

  /**
   * Main cart validation method - checks if cart contains items missing consent properties
   * Shows or hides consent checkbox based on cart contents and user location
   */
  checkCartForConsentProducts() {
    // Prevent concurrent checks and rapid re-checks (debounce)
    const now = Date.now();
    if (this.isCheckingCart) {
      if (this.debug) console.log('⏸️ Cart check already in progress, skipping...');
      return;
    }
    
    // Debounce: don't check more than once per 500ms
    if (now - this.lastCartCheckTime < 500) {
      if (this.debug) console.log('⏸️ Cart check debounced, skipping...');
      return;
    }
    
    this.isCheckingCart = true;
    this.lastCartCheckTime = now;
    
    try {
      // Skip if user doesn't require consent
      if (!this.requiresConsent()) {
        if (this.debug) console.log('ℹ️ User does not require consent, hiding checkbox');
        this.hideCartConsentCheckbox();
        return;
      }

      if (this.debug) console.log('✅ User requires consent, checking cart items...');

      const cartItems = this.getCartItems();
      
      if (this.debug) console.log('🛒 Cart items:', cartItems.length);
      if (this.debug) console.log('🛒 window.cart:', window.cart);
      if (this.debug) console.log('🛒 Cart page check:', window.location.pathname);
      
      // If cart is empty, still show consent checkbox if we're on cart page
      // This ensures consent is ready when user adds items
      const isCartPage = window.location.pathname === '/cart' || window.location.pathname.includes('/cart');
      
      if (cartItems.length === 0 && !isCartPage) {
        if (this.debug) console.log('ℹ️ Cart is empty and not on cart page, hiding consent checkbox');
        this.hideCartConsentCheckbox();
        return;
      }
      
      if (cartItems.length === 0 && isCartPage) {
        if (this.debug) console.log('ℹ️ Cart appears empty but on cart page - showing consent checkbox anyway');
        // Show consent checkbox even if cart is empty - user might add items
        this.showCartConsentCheckbox();
        return;
      }

      // Check if any cart items are missing consent properties
      const itemsNeedingConsent = cartItems.filter(item => {
        const productId = this.getProductIdFromItem(item);
        
        // First check: Does this product require consent?
        let productRequiresConsent = false;
        if (this.productIds.length > 0) {
          // Specific products are configured - only check those
          productRequiresConsent = this.productRequiresConsent(productId);
        } else {
          // No specific products configured - all products need consent for this state
          productRequiresConsent = true;
        }
        
        // If product doesn't require consent, skip it
        if (!productRequiresConsent) return false;
        
        // Second check: Does this item already have consent?
        const hasConsent = this.itemHasConsent(item);
        
        // Item needs consent if it requires consent but doesn't have it
        return !hasConsent;
      });

      if (this.debug) {
        console.log('🔍 Cart consent check results:');
        console.log('- Total items in cart:', cartItems.length);
        console.log('- Items needing consent:', itemsNeedingConsent.length);
        console.log('- Items needing consent details:', itemsNeedingConsent.map(item => ({
          productId: this.getProductIdFromItem(item),
          hasConsent: this.itemHasConsent(item),
          properties: item.properties
        })));
      }

      // Show consent UI only if there are items that need consent but don't have it
      if (itemsNeedingConsent.length > 0) {
        if (this.debug) console.log(`✅ Showing consent checkbox - ${itemsNeedingConsent.length} items need consent`);
        // Only show if not already shown to prevent DOM changes that trigger observers
        if (!this.consentCheckboxShown) {
          this.showCartConsentCheckbox();
        }
      } else {
        if (this.debug) console.log('ℹ️ All items have consent or don\'t require it, hiding checkbox');
        // Only hide if currently shown to prevent DOM changes that trigger observers
        if (this.consentCheckboxShown) {
          this.hideCartConsentCheckbox();
        }
      }
    } finally {
      this.isCheckingCart = false;
    }
  }

  /**
   * Checks if a cart item has consent line item property
   * 
   * @param {CartItem} item - Cart item to check
   * @returns {boolean} True if item has consent property
   */
  itemHasConsent(item) {
    if (!item.properties) return false;
    
    // Look for consent-related properties (including hidden ones with underscore)
    const consentKeys = ['_Consent', '_consent', 'Consent', 'Age Verification', 'Legal Consent'];
    const hasConsent = consentKeys.some(key => 
      item.properties[key] && 
      item.properties[key] !== '' && 
      item.properties[key] !== 'false' &&
      item.properties[key] !== 'No'
    );
    
    if (this.debug && hasConsent) {
      console.log(`✅ Item has consent:`, {
        productId: this.getProductIdFromItem(item),
        consentProperties: Object.keys(item.properties).filter(key => 
          consentKeys.includes(key) && item.properties[key]
        )
      });
    }
    
    return hasConsent;
  }

  /**
   * Retrieves current cart items from multiple possible sources
   * Tries window.cart object first, then falls back to DOM parsing
   * 
   * @returns {CartItem[]} Array of cart item objects
   */
  getCartItems() {
    // Primary method: Use global cart object (Shopify themes) - fastest
    if (window.cart?.items?.length > 0) {
      if (this.debug) console.log('📦 Found cart items from window.cart:', window.cart.items.length);
      return window.cart.items;
    }

    // Fallback: Parse cart items from DOM elements (only if window.cart not available)
    const cartItems = [];
    // Use more specific selectors for better performance
    const cartElements = document.querySelectorAll('tr.cart-item, .horizontal-product[data-variant-id]');
    
    for (const element of cartElements) {
      const variantId = element.getAttribute('data-variant-id');
      const productId = element.getAttribute('data-product-id');
      const lineId = element.id?.replace('CartItem-', '').replace('CartDrawer-Item-', '') || 
                    element.getAttribute('data-index');
      
      if (variantId || productId || lineId) {
        cartItems.push({
          id: lineId,
          variant_id: variantId,
          product_id: productId,
          handle: element.getAttribute('data-handle'),
          properties: {} // DOM parsing doesn't include properties
        });
      }
    }

    if (cartItems.length > 0) {
      if (this.debug) console.log('📦 Found cart items from DOM:', cartItems.length);
      return cartItems;
    }

    if (this.debug && (window.location.pathname === '/cart' || window.location.pathname.includes('/cart'))) {
      console.log('📦 On cart page but no items detected - cart may be loading or empty');
    }

    return cartItems;
  }

  /**
   * Extracts product ID from cart item object (handles multiple formats)
   * 
   * @param {CartItem} item - Cart item object
   * @returns {string|number|undefined} Product ID
   */
  getProductIdFromItem(item) {
    return item.product_id || 
           item.product?.id || 
           item.variant?.product_id ||
           item.id;
  }

  /**
   * Shows consent checkbox in both cart drawer and cart page
   * Uses existing Liquid-rendered checkbox for cart page, creates dynamically for drawer
   */
  showCartConsentCheckbox() {
    // Skip if already shown to prevent unnecessary DOM changes
    if (this.consentCheckboxShown) {
      if (this.debug) console.log('ℹ️ Consent checkbox already shown, skipping...');
      return;
    }
    
    if (this.debug) console.log('📋 Showing cart consent checkbox...');
    
    // Use cached element or find cart page consent container (rendered in Liquid)
    if (!this._cartPageConsent) {
      this._cartPageConsent = this._mainCart?.querySelector('[data-consent-container="page"]') || null;
    }
    
    if (this._cartPageConsent) {
      if (this.debug) console.log('✅ Found cart page consent container');
      // Only setup if not already set up
      if (!this._cartPageConsent._checkoutButton) {
        this.setupExistingConsentCheckbox(this._cartPageConsent, 'page');
      } else {
        // Just show it if already set up
        this._cartPageConsent.style.display = 'block';
      }
      this.consentCheckboxShown = true;
    } else {
      if (this.debug) console.log('⚠️ Cart page consent container not found');
    }

    // Use cached cart drawer element
    if (this._cartDrawer) {
      // Check if drawer already has consent checkbox
      const drawerConsent = this._cartDrawer.querySelector('[data-consent-container="drawer"]');
      if (!drawerConsent) {
        if (this.debug) console.log('✅ Adding consent checkbox to cart drawer');
        this.addConsentCheckboxToContainer(this._cartDrawer, 'drawer');
        this.consentCheckboxShown = true;
      } else {
        if (this.debug) console.log('✅ Found existing drawer consent container');
        if (!drawerConsent._checkoutButton) {
          this.setupExistingConsentCheckbox(drawerConsent, 'drawer');
        }
        this.consentCheckboxShown = true;
      }
    }
  }

  /**
   * Sets up event handlers for existing consent checkbox (rendered in Liquid)
   * 
   * @param {HTMLElement} consentContainer - Existing consent container element
   * @param {string} type - Container type ('drawer' or 'page')
   */
  setupExistingConsentCheckbox(consentContainer, type) {
    if (this.debug) console.log(`🔧 Setting up existing consent checkbox for ${type}`);
    
    // Update consent text with state info and custom text
    const userState = this.userLocation?.region || this.userLocation?.region_code;
    const stateLabel = consentContainer.querySelector('.cart-consent-state-label');
    if (stateLabel) {
      stateLabel.textContent = `Required Consent (${userState})`;
      if (this.debug) console.log(`✅ Updated state label to: Required Consent (${userState})`);
    }
    
    // Update consent text if custom text is provided
    const consentTextSpan = consentContainer.querySelector('.cart-consent-text');
    if (consentTextSpan && this.consentText) {
      // Use innerHTML to support HTML in consent text (already escaped from Liquid)
      consentTextSpan.innerHTML = this.consentText;
      if (this.debug) console.log('✅ Updated consent text');
    }

    // Find checkout button
    const checkoutButton = consentContainer.closest('main-cart, cart-drawer').querySelector('button[name="checkout"]');
    if (!checkoutButton) {
      if (this.debug) console.log(`⚠️ No checkout button found for ${type}`);
      return;
    }

    // Show the consent container
    consentContainer.style.display = 'block';
    if (this.debug) console.log(`✅ Consent container displayed for ${type}`);

    // Get checkbox and messages
    const checkbox = consentContainer.querySelector('.cart-consent-checkbox');
    const errorMsg = consentContainer.querySelector('.cart-consent-error');
    const processingMsg = consentContainer.querySelector('.cart-consent-processing');

    if (!checkbox) {
      if (this.debug) console.log(`⚠️ No checkbox found in consent container`);
      return;
    }

    // Set up event handlers
    this.setupConsentCheckboxHandlers(consentContainer, checkbox, errorMsg, processingMsg, checkoutButton, type);
    if (this.debug) console.log(`✅ Event handlers set up for ${type}`);
  }

  /**
   * Creates and inserts consent checkbox UI into specified cart container (for drawer only)
   * Sets up event handlers for consent state management and checkout interception
   * 
   * @param {HTMLElement} container - Cart container element (drawer only)
   * @param {string} type - Container type ('drawer' only)
   */
  addConsentCheckboxToContainer(container, type) {
    // Skip if consent checkbox already exists for this container type
    const existingConsent = container.querySelector(`[data-consent-container="${type}"]`);
    if (existingConsent) {
      if (this.debug) console.log(`ℹ️ Consent checkbox already exists for ${type}`);
      return;
    }

    // Find the checkout button - try multiple selectors for better compatibility
    let checkoutButton = container.querySelector('button[name="checkout"]');
    
    // If not found in container, search more broadly (for cart page with form attribute)
    if (!checkoutButton) {
      checkoutButton = container.querySelector('button[name="checkout"][form="cart"]');
    }
    
    // If still not found, try form-based selector
    if (!checkoutButton) {
      const form = container.querySelector('form[action*="/cart"], form[action*="/checkout"]');
      if (form) {
        checkoutButton = form.querySelector('button[name="checkout"], button[type="submit"]');
      }
    }

    // For cart page: search within the entire main-cart element
    if (!checkoutButton && container.tagName === 'MAIN-CART') {
      checkoutButton = container.querySelector('button[name="checkout"]');
    }

    // Last resort: find any submit button in a cart-related form
    if (!checkoutButton) {
      checkoutButton = container.querySelector('button[type="submit"][form="cart"]');
    }

    if (!checkoutButton) {
      if (this.debug) console.log(`⚠️ No checkout button found in ${type} container:`, container);
      return;
    }

    if (this.debug) console.log(`✅ Found checkout button in ${type}:`, checkoutButton);

    // Create consent container with styled layout
    const consentContainer = document.createElement('div');
    consentContainer.className = 'cart-consent-container grid gap-3 p-4 border border-opacity-20 rounded-lg bg-opacity-5';
    consentContainer.setAttribute('data-consent-container', type);

    const userState = this.userLocation?.region || this.userLocation?.region_code;
    
    // Build consent UI HTML
    consentContainer.innerHTML = `
      <div class="flex items-start gap-3">
        <input 
          type="checkbox" 
          id="cart-consent-${type}" 
          class="cart-consent-checkbox mt-1"
          required
        />
        <label for="cart-consent-${type}" class="text-sm leading-tight cursor-pointer">
          <strong>Required Consent (${userState})</strong><br>
          ${this.consentText}
        </label>
      </div>
      <div class="cart-consent-error text-xs text-red-600 hidden">
        Please provide your consent before proceeding to checkout.
      </div>
      <div class="cart-consent-processing text-xs text-blue-600 hidden">
        Adding consent to cart items...
      </div>
    `;

    // IMPORTANT: Disable checkout button FIRST before inserting consent UI
    // This prevents any race conditions where the button might be enabled
    this.disableSpecificCheckoutButton(checkoutButton);

    // Insert consent UI directly above checkout button
    // For cart page: insert in the buttons block (grid container) before checkout button
    // For drawer: insert before checkout button in its form
    const buttonsBlock = checkoutButton.closest('[class*="grid"][class*="gap"], [class*="flex"][class*="gap"]');
    
    if (buttonsBlock) {
      // Insert directly before checkout button in the buttons block
      buttonsBlock.insertBefore(consentContainer, checkoutButton);
      if (this.debug) console.log(`✅ Inserted consent in buttons block before checkout button`);
    } else {
      // Fallback: insert before checkout button in its parent
      checkoutButton.parentNode.insertBefore(consentContainer, checkoutButton);
      if (this.debug) console.log(`✅ Inserted consent before checkout button (fallback)`);
    }

    // Set up event handlers
    const checkbox = consentContainer.querySelector('.cart-consent-checkbox');
    const errorMsg = consentContainer.querySelector('.cart-consent-error');
    const processingMsg = consentContainer.querySelector('.cart-consent-processing');

    // Set up handlers
    this.setupConsentCheckboxHandlers(consentContainer, checkbox, errorMsg, processingMsg, checkoutButton, type);
  }

  /**
   * Sets up event handlers for consent checkbox (shared by both Liquid and dynamic checkboxes)
   * 
   * @param {HTMLElement} consentContainer - Consent container element
   * @param {HTMLElement} checkbox - Checkbox input element
   * @param {HTMLElement} errorMsg - Error message element
   * @param {HTMLElement} processingMsg - Processing message element
   * @param {HTMLElement} checkoutButton - Checkout button element
   * @param {string} type - Container type ('drawer' or 'page')
   */
  setupConsentCheckboxHandlers(consentContainer, checkbox, errorMsg, processingMsg, checkoutButton, type) {
    // Handle consent checkbox changes
    checkbox.addEventListener('change', async (e) => {
      if (e.target.checked) {
        try {
          processingMsg.classList.remove('hidden');
          errorMsg.classList.add('hidden');
          
          // Disable checkout button while processing
          this.disableSpecificCheckoutButton(checkoutButton);
          
          // Add consent to all cart items
          await this.addConsentToCartItems();
          
          // Enable checkout button after consent is added
          this.enableSpecificCheckoutButton(checkoutButton);
          processingMsg.classList.add('hidden');
          
          // Emit consent given event
          this.dispatchEvent(new CustomEvent('consent-given', {
            detail: { location: this.userLocation },
            bubbles: true
          }));
          
        } catch (error) {
          console.error('Error adding consent to cart:', error);
          errorMsg.textContent = 'Error processing consent. Please try again.';
          errorMsg.classList.remove('hidden');
          processingMsg.classList.add('hidden');
          checkbox.checked = false;
          this.disableSpecificCheckoutButton(checkoutButton);
        }
      } else {
        // Consent unchecked - disable checkout
        errorMsg.classList.add('hidden');
        this.disableSpecificCheckoutButton(checkoutButton);
      }
    });

    // Set up checkout interception
    const clickHandler = (e) => {
      if (!checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        errorMsg.classList.remove('hidden');
        checkbox.focus();
        checkbox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return false;
      }
    };

    const formHandler = (e) => {
      if (!checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        errorMsg.classList.remove('hidden');
        checkbox.focus();
        checkbox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return false;
      }
    };

    // Disable checkout button immediately
    this.disableSpecificCheckoutButton(checkoutButton);

    // Set up checkout interception with capture phase for better reliability
    checkoutButton.addEventListener('click', clickHandler, true);
    
    // Also intercept form submission
    const form = checkoutButton.form || checkoutButton.closest('form');
    if (form) {
      form.addEventListener('submit', formHandler, true);
    }
    
    // Store reference to this specific button and handlers for cleanup
    consentContainer._checkoutButton = checkoutButton;
    consentContainer._clickHandler = clickHandler;
    consentContainer._formHandler = formHandler;
    consentContainer._form = form;
  }

  /**
   * Adds consent properties to all cart items that require consent
   * Uses Shopify Cart API to update line item properties
   * 
   * @async
   * @returns {Promise<void>}
   */
  async addConsentToCartItems() {
    if (this.isProcessingConsent) return;
    this.isProcessingConsent = true;

    try {
      // Get fresh cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      if (!cartData.items || cartData.items.length === 0) return;

      // Prepare consent data
      const consentValue = `Agreed - ${this.userLocation?.region_code || 'Unknown'} - ${new Date().toISOString()}`;
      const updates = {};

      // Process each cart item
      for (const item of cartData.items) {
        const productId = this.getProductIdFromItem(item);
        
        // Check if this item needs consent
        let needsConsent = false;
        if (this.productIds.length > 0) {
          needsConsent = this.productRequiresConsent(productId);
        } else {
          // If no specific products configured, all items need consent
          needsConsent = true;
        }

        if (needsConsent && !this.itemHasConsent(item)) {
          // Use line item key (line number) for updates
          const lineNumber = cartData.items.indexOf(item) + 1;
          const consentTextClean = this.consentText ? this.consentText.replace(/<[^>]*>/g, '').trim() : '';
          updates[`attributes[_Consent][${lineNumber}]`] = consentValue;
          updates[`attributes[_Consent Location][${lineNumber}]`] = `${this.userLocation?.region || 'Unknown'}, ${this.userLocation?.country || 'Unknown'}`;
          updates[`attributes[_Consent Text][${lineNumber}]`] = consentTextClean;
        }
      }

      if (Object.keys(updates).length === 0) {
        if (this.debug) console.log('No items need consent updates');
        return;
      }

      // Update cart with consent properties
      if (this.debug) console.log('Updating cart with consent:', updates);
      
      // Use line item properties update approach
      await this.updateCartItemsWithConsent(cartData.items, consentValue);
      
    } catch (error) {
      console.error('Error adding consent to cart items:', error);
      throw error;
    } finally {
      this.isProcessingConsent = false;
    }
  }

  /**
   * Updates cart items with consent properties using Shopify Cart API
   * 
   * @param {CartItem[]} items - Cart items to update
   * @param {string} consentValue - Consent value to add
   * @async
   * @returns {Promise<void>}
   */
  async updateCartItemsWithConsent(items, consentValue) {
    // We need to remove items and re-add them with consent properties
    // This is the only reliable way to add line item properties to existing cart items
    
    const itemsToReAdd = [];
    
    for (const item of items) {
      const productId = this.getProductIdFromItem(item);
      
      // Check if this item needs consent
      let needsConsent = false;
      if (this.productIds.length > 0) {
        needsConsent = this.productRequiresConsent(productId);
      } else {
        needsConsent = true;
      }

      // Only process items that need consent AND don't already have it
      if (needsConsent && !this.itemHasConsent(item)) {
        // Prepare item for re-adding with consent (using underscore prefix to hide)
        // Strip HTML tags from consent text for storage (keep it clean)
        const consentTextClean = this.consentText ? this.consentText.replace(/<[^>]*>/g, '').trim() : '';
        
        const itemData = {
          id: item.variant_id,
          quantity: item.quantity,
          properties: {
            '_Consent': consentValue,
            '_Consent Location': `${this.userLocation?.region || 'Unknown'}, ${this.userLocation?.country || 'Unknown'}`,
            '_Consent Text': consentTextClean
          }
        };
        
        // Copy any existing properties
        if (item.properties) {
          Object.assign(itemData.properties, item.properties);
        }
        
        itemsToReAdd.push({
          originalItem: item,
          newItemData: itemData
        });
      }
    }

    if (this.debug) {
      console.log('📝 Items to update with consent:', itemsToReAdd.length);
    }

    if (itemsToReAdd.length === 0) {
      if (this.debug) console.log('ℹ️ No items need consent updates - all items already have consent or don\'t require it');
      return;
    }

    // Remove original items
    for (const { originalItem } of itemsToReAdd) {
      await this.removeCartItem(originalItem.key);
    }

    // Re-add items with consent properties
    for (const { newItemData } of itemsToReAdd) {
      await this.addToCart(newItemData);
    }

    // Get fresh cart data and refresh properly
    await this.refreshCartProperly();
    
    // Re-check cart after refresh to ensure consent UI is still shown/hidden correctly
    setTimeout(() => this.checkCartForConsentProducts(), 500);
  }

  /**
   * Removes an item from cart
   * 
   * @param {string} itemKey - Cart item key
   * @async
   * @returns {Promise<void>}
   */
  async removeCartItem(itemKey) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to remove item: ${response.statusText}`);
    }
    
    return response.json();
  }

  /**
   * Adds an item to cart with properties
   * 
   * @param {Object} itemData - Item data with properties
   * @async
   * @returns {Promise<void>}
   */
  async addToCart(itemData) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(itemData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to add item: ${response.statusText}`);
    }
    
    return response.json();
  }

  /**
   * Properly refreshes cart display with correct data structure
   * @async
   * @returns {Promise<void>}
   */
  async refreshCartProperly() {
    try {
      // Get fresh cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      if (this.debug) console.log('Fresh cart data:', cartData);
      
      // Update window.cart if it exists
      if (window.cart) {
        window.cart = cartData;
      }
      
      // Try different approaches for cart refresh in order of preference
      let refreshSuccess = false;
      
      // Method 1: Use theme's built-in cart refresh if available
      if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
        try {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'consent-checker',
            cart: cartData
          });
          refreshSuccess = true;
          if (this.debug) console.log('✅ Cart refreshed via theme pubsub');
        } catch (error) {
          if (this.debug) console.log('❌ Theme pubsub failed:', error);
        }
      }
      
      // Method 2: Trigger standard DOM events
      if (!refreshSuccess) {
        try {
          // Trigger cart refresh without opening drawer
          document.dispatchEvent(new CustomEvent('cart:refresh', {
            detail: {
              cart: cartData,
              open: false
            },
            bubbles: true
          }));
          
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
              cart: cartData
            },
            bubbles: true
          }));
          
          refreshSuccess = true;
          if (this.debug) console.log('✅ Cart refreshed via DOM events');
        } catch (error) {
          if (this.debug) console.log('❌ DOM events failed:', error);
        }
      }
      
      // Method 3: Manual DOM update as fallback
      if (!refreshSuccess) {
        this.manualCartRefresh(cartData);
        if (this.debug) console.log('✅ Cart refreshed manually');
      }
      
    } catch (error) {
      console.error('Error refreshing cart:', error);
      // Instead of page reload, try manual refresh
      this.manualCartRefresh();
    }
  }

  /**
   * Manually refreshes cart display by updating DOM elements
   * @param {Object} cartData - Cart data object
   */
  manualCartRefresh(cartData = null) {
    try {
      // Update cart count if element exists
      const cartCountElements = document.querySelectorAll('cart-count, .cart-count, [data-cart-count]');
      if (cartData && cartCountElements.length > 0) {
        cartCountElements.forEach(element => {
          if (element.tagName.toLowerCase() === 'cart-count') {
            element.innerText = cartData.item_count || 0;
          } else {
            element.textContent = cartData.item_count || 0;
          }
        });
      }
      
      // Force refresh of cart drawer/page content (use cached elements)
      if (this._cartDrawer) {
        const sectionElement = this._cartDrawer.querySelector('[data-section-id]');
        if (sectionElement) {
          this.refreshCartSection(sectionElement.getAttribute('data-section-id'));
        }
      }
      
      if (this._mainCart) {
        const sectionElement = this._mainCart.querySelector('[data-section-id]');
        if (sectionElement) {
          this.refreshCartSection(sectionElement.getAttribute('data-section-id'));
        }
      }
      
      if (this.debug) console.log('Manual cart refresh completed');
      
    } catch (error) {
      console.error('Manual cart refresh failed:', error);
      // Only as last resort, and with user confirmation
      if (confirm('Cart update completed but display refresh failed. Reload page to see changes?')) {
        window.location.reload();
      }
    }
  }

  /**
   * Refreshes a specific cart section using Shopify's section rendering
   * @param {string} sectionId - Section ID to refresh
   */
  async refreshCartSection(sectionId) {
    try {
      const response = await fetch(`/?sections=${sectionId}`);
      const sections = await response.json();
      
      if (sections[sectionId]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections[sectionId], 'text/html');
        const newContent = doc.querySelector(`[data-section-id="${sectionId}"]`);
        const currentSection = document.querySelector(`[data-section-id="${sectionId}"]`);
        
        if (newContent && currentSection) {
          currentSection.innerHTML = newContent.innerHTML;
          if (this.debug) console.log(`✅ Refreshed section: ${sectionId}`);
        }
      }
    } catch (error) {
      if (this.debug) console.log(`❌ Section refresh failed for ${sectionId}:`, error);
    }
  }

  /**
   * Disables a specific checkout button
   * @param {HTMLElement} button - The checkout button to disable
   */
  disableSpecificCheckoutButton(button) {
    if (!button) return;
    
    button.style.opacity = '0.5';
    button.style.pointerEvents = 'none';
    button.setAttribute('disabled', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('consent-disabled');
    
    if (this.debug) console.log('🔒 Disabled checkout button:', button);
  }

  /**
   * Enables a specific checkout button
   * @param {HTMLElement} button - The checkout button to enable
   */
  enableSpecificCheckoutButton(button) {
    if (!button) return;
    
    button.style.opacity = '';
    button.style.pointerEvents = '';
    button.removeAttribute('disabled');
    button.removeAttribute('aria-disabled');
    button.classList.remove('consent-disabled');
    
    if (this.debug) console.log('🔓 Enabled checkout button:', button);
  }

  /**
   * Visually disables all checkout buttons while consent is required
   */
  disableCheckout() {
    // Use cached containers for better performance
    const containers = [];
    if (this._cartDrawer) containers.push(this._cartDrawer);
    if (this._mainCart) containers.push(this._mainCart);
    
    // Also check forms
    const forms = document.querySelectorAll('form[action*="/cart"], form[action*="/checkout"]');
    containers.push(...forms);
    
    for (const container of containers) {
      const checkoutButton = container.querySelector('button[name="checkout"]');
      if (checkoutButton) {
        const consentCheckbox = container.querySelector('.cart-consent-checkbox');
        if (consentCheckbox && !consentCheckbox.checked) {
          this.disableSpecificCheckoutButton(checkoutButton);
        }
      }
    }
  }

  /**
   * Re-enables checkout buttons after consent is given
   */
  enableCheckout() {
    // Use cached containers for better performance
    const containers = [];
    if (this._cartDrawer) containers.push(this._cartDrawer);
    if (this._mainCart) containers.push(this._mainCart);
    
    // Also check forms
    const forms = document.querySelectorAll('form[action*="/cart"], form[action*="/checkout"]');
    containers.push(...forms);
    
    for (const container of containers) {
      const checkoutButton = container.querySelector('button[name="checkout"]');
      if (checkoutButton) {
        const consentCheckbox = container.querySelector('.cart-consent-checkbox');
        // Only enable if checkbox is checked or doesn't exist (no consent required)
        if (!consentCheckbox || consentCheckbox.checked) {
          this.enableSpecificCheckoutButton(checkoutButton);
        }
      }
    }
  }

  /**
   * Hides consent UI elements and re-enables checkout
   * For Liquid-rendered checkboxes, just hides them. For dynamic ones, removes them.
   */
  hideCartConsentCheckbox() {
    // Skip if already hidden to prevent unnecessary DOM changes
    if (!this.consentCheckboxShown) {
      if (this.debug) console.log('ℹ️ Consent checkbox already hidden, skipping...');
      return;
    }
    
    const existingContainers = document.querySelectorAll('[data-consent-container]');
    for (const container of existingContainers) {
      // Check if consent was given before enabling button
      const consentCheckbox = container.querySelector('.cart-consent-checkbox');
      const hadConsent = consentCheckbox?.checked;
      
      // Only re-enable the checkout button if consent was given
      if (container._checkoutButton && hadConsent) {
        this.enableSpecificCheckoutButton(container._checkoutButton);
      }
      
      // Clean up event listeners if stored
      if (container._clickHandler && container._checkoutButton) {
        container._checkoutButton.removeEventListener('click', container._clickHandler, true);
      }
      if (container._formHandler && container._form) {
        container._form.removeEventListener('submit', container._formHandler, true);
      }
      
      // For Liquid-rendered checkboxes, just hide them. For dynamic ones, remove them.
      const isLiquidRendered = container.getAttribute('data-consent-container') === 'page';
      if (isLiquidRendered) {
        container.style.display = 'none';
        // Reset checkbox state
        if (consentCheckbox) {
          consentCheckbox.checked = false;
        }
      } else {
        container.remove();
      }
    }
    
    this.consentCheckboxShown = false;
    
    // Enable buttons that don't have consent requirements (use cached containers)
    const containers = [];
    if (this._cartDrawer) containers.push(this._cartDrawer);
    if (this._mainCart) containers.push(this._mainCart);
    const forms = document.querySelectorAll('form[action*="/cart"]');
    containers.push(...forms);
    
    for (const container of containers) {
      const checkoutButton = container.querySelector('button[name="checkout"]');
      if (checkoutButton) {
        const consentCheckbox = container.querySelector('.cart-consent-checkbox');
        const consentContainer = container.querySelector('[data-consent-container]');
        // Only enable if no consent checkbox exists or consent container is hidden
        if (!consentCheckbox || (consentContainer && consentContainer.style.display === 'none')) {
          this.enableSpecificCheckoutButton(checkoutButton);
        }
      }
    }
  }

  /**
   * Sets a cookie with specified name, value, and expiry
   * 
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Days until expiry
   */
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  /**
   * Retrieves cookie value by name
   * 
   * @param {string} name - Cookie name to retrieve
   * @returns {string|null} Cookie value or null if not found
   */
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // ==================== PUBLIC API METHODS ====================

  /**
   * Gets the current user location data
   * 
   * @public
   * @returns {LocationData|null} User's location data or null if not detected
   */
  getLocationData() {
    return this.userLocation;
  }

  /**
   * Checks if current user requires consent based on their location
   * 
   * @public
   * @returns {boolean} True if user is from a consent-required state
   */
  requiresConsent() {
    if (!this.userLocation) {
      if (this.debug) console.log('⚠️ User location not yet loaded');
      return false;
    }
    
    const requires = this.isFromConsentState(this.userLocation);
    if (this.debug) console.log('🔍 requiresConsent() result:', requires, 'for location:', this.userLocation);
    return requires;
  }

  /**
   * Gets the list of product IDs that require consent
   * 
   * @public
   * @returns {string[]} Array of product IDs requiring consent
   */
  getConsentProductIds() {
    return this.productIds;
  }

  /**
   * Checks if a specific product requires consent for the current user
   * 
   * @public
   * @param {string|number} productId - Product ID to check
   * @returns {boolean} True if product requires consent for this user
   */
  productRequiresConsent(productId) {
    if (!this.requiresConsent()) return false;
    
    // If no specific products configured, all products require consent
    if (this.productIds.length === 0) return true;
    
    // Check if product ID is in the consent list
    const productIdStr = String(productId);
    return this.productIds.some(id => String(id) === productIdStr);
  }

  /**
   * Development method to clear all caches for testing
   * 
   * @public
   */
  clearAllCaches() {
    console.log('🧹 Clearing all caches for fresh test...');
    sessionStorage.removeItem('user_location_data');
    this.deleteCookie(this.locationCookieName);
    console.log('✅ All caches cleared');
  }

  /**
   * Deletes a cookie by setting its expiry to the past
   * 
   * @param {string} name - Cookie name to delete
   */
  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }
}

// Register the custom element
if (!customElements.get('consent-checker')) {
  customElements.define('consent-checker', ConsentChecker);
}

/**
 * Global cart event listeners for automatic consent checking
 * Listens for various cart update events and triggers consent validation
 */
document.addEventListener('DOMContentLoaded', function() {
  // Listen for various cart events
  ['cart:refresh', 'cart:updated', 'cart:change'].forEach(eventName => {
    document.addEventListener(eventName, function() {
      const consentChecker = document.querySelector('consent-checker');
      if (consentChecker) {
        setTimeout(() => consentChecker.checkCartForConsentProducts(), 100);
      }
    });
  });
});